import { Row, Col } from "antd";
import { withTranslation, TFunction } from "react-i18next";
import { SvgIcon } from "../../common/SvgIcon";
import Container from "../../common/Container";
import styled from "styled-components";
import {
  FooterSection,
  Para,
  Language,
} from "./styles";

interface SocialLinkProps {
  href?: string;
  src: string;
  label: string;
  backgroundColor: string;
  onClick?: () => void;
  text: string;
  iconWidth?: string;
  iconHeight?: string;
}

const FooterSectionStyled = styled(FooterSection)`
  overflow: visible !important;
  padding-bottom: 2rem !important;
`;

const ContactWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
  overflow: visible;
`;

const LanguageStyled = styled(Language)`
  font-size: 1.8rem;
  margin-bottom: 0.15rem;

  @media (max-width: 992px) {
    font-size: 1.6rem;
    text-align: center;
  }

  @media (max-width: 576px) {
    font-size: 1.35rem;
  }
`;

const ParaStyled = styled(Para)`
  font-size: 1.25rem;
  margin-bottom: 0.25rem !important;

  @media (max-width: 992px) {
    font-size: 1.15rem;
    text-align: center;
  }

  @media (max-width: 576px) {
    font-size: 0.98rem;
    margin-bottom: 0.2rem !important;
  }
`;

const CardsWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  overflow: visible;
  padding: 0.35rem 0.5rem 1.1rem;
  margin-bottom: 0.35rem;
  box-sizing: border-box;

  @media (max-width: 576px) {
    padding: 0.3rem 0.35rem 1rem;
  }
`;

const IconWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.7rem;
  width: 100%;
  max-width: 380px;
  align-items: stretch;
  overflow: visible;

  @media (max-width: 576px) {
    gap: 0.55rem;
    max-width: 340px;
  }
`;

const ActionCard = styled.div`
  width: 100%;
  min-width: 0;
  min-height: 118px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background: #ffffff;
  border-radius: 16px;
  padding: 0.7rem 0.5rem 0.85rem;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
  box-sizing: border-box;
  overflow: visible;

  @media (max-width: 576px) {
    min-height: 108px;
    padding: 0.6rem 0.45rem 0.75rem;
    border-radius: 14px;
  }
`;

const ActionText = styled.p`
  margin: 0 0 0.55rem 0;
  font-size: 0.84rem;
  font-weight: 700;
  color: #000000;
  text-align: center;
  line-height: 1.15;
  word-break: break-word;

  @media (max-width: 576px) {
    font-size: 0.76rem;
    margin-bottom: 0.45rem;
  }
`;

const AprobacionWrapper = styled.div`
  background-color: rgb(46, 115, 243);
  font-weight: 700;
  font-size: 1.6rem;
  color: white;
  text-align: center;
  padding: 15px 20px;
  border-radius: 15px;
  max-width: 700px;
  margin: 12px auto 15px auto;
  box-shadow: 0 4px 15px rgba(144, 80, 190, 0.6);
  font-family: "Segoe UI", sans-serif;
  letter-spacing: 1.5px;

  span {
    font-weight: 900;
  }

  @media (max-width: 576px) {
    font-size: 1.2rem;
    padding: 12px 15px;
  }
`;

const AttributionWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  text-align: center;
  margin-top: 2rem;
  font-size: 0.8rem;
  color: #555;
`;

const Footer = ({ t }: { t: TFunction }) => {
  const handleOpenKommoChat = () => {
    if (
      typeof window !== "undefined" &&
      typeof (window as any).crmPlugin === "function"
    ) {
      (window as any).crmPlugin("runChatShow");
    }
  };

  const commonStyle = (backgroundColor: string) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "50px",
    height: "50px",
    borderRadius: "50%",
    backgroundColor,
    transition: "all 0.3s ease-in-out",
    border: "none",
    cursor: "pointer",
    padding: 0,
    boxShadow: "0 4px 10px rgba(0,0,0,0.14)",
    flexShrink: 0,
    marginTop: "auto",
  });

  const SocialLink = ({
    href,
    src,
    label,
    backgroundColor,
    onClick,
    text,
    iconWidth = "26px",
    iconHeight = "26px",
  }: SocialLinkProps) => {
    const icon = (
      <SvgIcon src={src} width={iconWidth} height={iconHeight} />
    );

    const content = onClick ? (
      <button
        type="button"
        aria-label={label}
        onClick={onClick}
        style={commonStyle(backgroundColor)}
      >
        {icon}
      </button>
    ) : (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label}
        style={commonStyle(backgroundColor)}
      >
        {icon}
      </a>
    );

    return (
      <ActionCard>
        <ActionText>{text}</ActionText>
        {content}
      </ActionCard>
    );
  };

  return (
    <>
      <FooterSectionStyled>
        <Container>
          <Row justify="center">
            <Col lg={10} md={16} sm={24} xs={24}>
              <ContactWrapper>
                <LanguageStyled>{t("Medios de Contacto")}</LanguageStyled>
                <ParaStyled>{t("Comunícate con Mi Canal Digital")}</ParaStyled>

                <CardsWrapper>
                  <IconWrapper>
                    <SocialLink
                      src="chat.png"
                      label="Chat Online"
                      text="Escribe a nuestro chat online"
                      backgroundColor="#ffffff"
                      onClick={handleOpenKommoChat}
                      iconWidth="26px"
                      iconHeight="26px"
                    />

                    <SocialLink
                      href="https://wa.me/+584124431231"
                      src="WS.avif"
                      label="WhatsApp"
                      text="Escribe a nuestro WhatsApp"
                      backgroundColor="#128C7E"
                      iconWidth="60px"
                      iconHeight="60px"
                    />
                  </IconWrapper>
                </CardsWrapper>
              </ContactWrapper>
            </Col>
          </Row>
        </Container>
      </FooterSectionStyled>

      <AprobacionWrapper>
        Página web aprobada por la Sudeaseg bajo el código: <span>TVP-2025-0698</span>
      </AprobacionWrapper>

      <AttributionWrapper>
        Página diseñada por:{" "}
        <a
          href="https://www.instagram.com/solveitvzla"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#128C7E", fontWeight: "bold" }}
        >
          SolveIT Venezuela{" "}
          <SvgIcon src="SIT.avif" width="20px" height="20px" />
        </a>
      </AttributionWrapper>
    </>
  );
};

export default withTranslation()(Footer);